/**
 * 学校作业接口（家长端为主）。路径与语义见 `docs/接口契约对齐.md` 的「作业」章节。
 *
 * | 方法 | 路径 | 说明 |
 * | --- | --- | --- |
 * | GET  | `/students/:id/homework?date=` | 取某天的作业单（科目 + 作业项 + 打卡状态 + 完成聚合） |
 * | PUT  | `/students/:id/homework?date=` | **整单保存**（每天录入走这个） |
 * | POST | `/students/:id/homework/copy`   | 「复制上次的结构」 |
 * | GET  | `/students/:id/homework/history?from=&to=` | 过往作业单列表（含**每天的科目名**，录入表单据此给"科目候选"） |
 * | GET  | `/students/:id/homework/overdue?limit=`   | 「超时」队列（**派生的只读视图**） |
 * | GET  | `/students/:id/homework/open?limit=`      | 「**还有效**」队列（还没到期、但已经不是今天布置的未完成项） |
 *
 * ## 三个**必须**在这里保真的语义（写错了就是 bug）
 *
 * 1. **`dueTimeSource === 'dayEnd'`**：科目没填截止时刻时，服务端把 `due_time` 落成
 *    `23:59`（见 `HOMEWORK_DEFAULT_DUE_TIME`），语义是"当天完成"。这个 23:59
 *    **不是家长填的值**，所以：
 *      - 界面展示走 `describeDueTime()` → 显示「当天完成」而不是 23:59；
 *      - 编辑回填走 `dueTimeInputOf()`（`@fht/shared` 的 `homework-draft.ts`）→
 *        截止时刻框留空，而不是把 23:59 当成"填过的"。
 *        判据放在 shared 而不是这里：读侧（展示）与写侧（回填 → 提交）
 *        必须共用同一份实现，见 `homeworkDayToDraft()` 的注释。
 *    这一对函数放在这里而不是页面里，是为了让"读"和"写"两条路径**共用同一个判据**，
 *    不会出现"展示时正确、回填时漏了"这种只错一半的情况。
 *
 *    ⚠️ 2026-10-16 起多了一个**日期**维度（`dueDate` = `node.due_date`，独立一列）：
 *    `23:59` 只说明"那一天结束前"，**哪一天**由 `dueDate` 决定，
 *    而且它可能与布置日不同（周五布置 → 周日截止）。
 *    所以"当天完成"这句话只在 `dueDate` 等于布置日时才成立 ——
 *    文案判据统一在 `@fht/shared` 的 `describeHomeworkDueText()`。
 *
 * 2. **`retained: true`**：有真实打卡记录、被移出这天的计划但**没被删除**（保留事实数据）。
 *    界面必须看起来像"已移除"（灰掉 + 「已移除（保留记录）」），并且**不能**让人以为
 *    它还会出现在孩子端。这里原样透传 `retained`，不做任何"美化"。
 *
 * 3. **同名重新加回来会复用原记录**（包括原打卡记录）：这是刻意的，
 *    所以界面**不能**把同名项当成新项渲染成空的 —— 直接把服务端返回的
 *    `status` / `checkin` 显示出来即可（见 `isHomeworkItemEmpty()`）。
 *
 * ## 响应形状的读取方式
 *
 * 后端返回的就是 `{ studentId, date, exists, sheet, subjects[], summary, permissions }`
 * （见 `homework.service.ts` 的 `HomeworkDayResult`），字段名逐字一致。
 * 这里**不做 `pickArray` 那样的模糊兜底**：契约是明确的，猜错字段名应该直接暴露，
 * 而不是悄悄渲染成空列表（`shape.ts` 的注释里记着这个教训）。
 */
import { checkinStatusOf, toNumber, toOptionalNumber } from './shape';
import { api } from './client';
import {
  describeHomeworkDueText,
  describeHomeworkOpenBadge,
  type CheckinStatus,
} from '@fht/shared';

// ---------------------------------------------------------------- 响应类型

/** 科目截止时刻的来源（与后端 `HomeworkDueTimeSource` 同值）。 */
export type HomeworkDueTimeSource = 'explicit' | 'duration' | 'dayEnd' | 'none';

export interface HomeworkCheckinView {
  checkinId: string;
  status: CheckinStatus;
  estimatedSeconds: number | null;
  actualSeconds: number;
  dueAt: string;
  startedAt: string | null;
  finishedAt: string | null;
  valueDone: number | null;
  note: string;
  skipReason: string;
  isMakeup: boolean;
  pointsAwarded: boolean;
  /** 派生：未终结且已过 `dueAt`。 */
  overdue: boolean;
  overdueSeconds: number;
}

export interface HomeworkItemView {
  habitId: string;
  subjectNodeId: string;
  name: string;
  pointsPerUnit: number;
  targetValue: number | null;
  unit: string | null;
  needApproval: boolean;
  /** false = 已被后续的整单保存移出计划（因有打卡记录而停用保留）。 */
  active: boolean;
  /** 与 `active` 相反（后端单独给出，界面读它更直白）。 */
  retained: boolean;
  status: CheckinStatus;
  checkin: HomeworkCheckinView | null;
  canCheckin: boolean;
  canApprove: boolean;
}

export interface SubjectCompletion {
  total: number;
  done: number;
  submitted: number;
  abandoned: number;
  remaining: number;
  completed: boolean;
}

export interface HomeworkSubjectView {
  nodeId: string;
  name: string;
  startTime: string | null;
  /** `node.due_time` 规范成 'HH:mm'（**可能是 dayEnd 的 23:59**，见模块头注释）。 */
  dueTime: string | null;
  durationMinutes: number | null;
  dueTimeSource: HomeworkDueTimeSource;
  /**
   * **截止日期**（'YYYY-MM-DD'）—— 与 `dueTime` 是两个维度：
   * 这一个是**哪一天**截止，那一个是**那一天几点**截止。
   *
   * 老数据（`node.due_date` = 布置日）等于这一天的日期，也就是"当天完成"。
   * ⚠️ 服务端缺失这个字段时归一成**空串**：那时一律按"与布置日同一天"处理
   * （`dueDateInputOf()` / `describeSubjectSchedule()` 都做这个回落），
   * 不会凭空显示一个"周日"。
   */
  dueDate: string;
  sortOrder: number;
  /** false = 这一科已不在当前计划里，只因下面有记录而整科保留。 */
  active: boolean;
  retained: boolean;
  items: HomeworkItemView[];
  completion: SubjectCompletion;
}

export interface HomeworkSummary {
  subjectCount: number;
  itemCount: number;
  doneCount: number;
  submittedCount: number;
  abandonedCount: number;
  remainingCount: number;
  completedSubjectCount: number;
  allCompleted: boolean;
}

export interface HomeworkSheetView {
  nodeId: string;
  date: string;
  name: string;
  createdAt: string;
}

export interface HomeworkDayResult {
  studentId: string;
  date: string;
  timeZone: string;
  /** false = 这一天没有作业单（还没录入，或整单已被清空）。 */
  exists: boolean;
  sheet: HomeworkSheetView | null;
  subjects: HomeworkSubjectView[];
  summary: HomeworkSummary;
  permissions: { canEdit: boolean; canCheckin: boolean; canApprove: boolean };
}

/** 一条"因已有打卡记录而未删除"的内容（响应与审计都用它）。 */
export interface HomeworkRetainedNote {
  kind: 'subject' | 'item';
  name: string;
  subjectName: string | null;
  nodeId: string | null;
  habitId: string | null;
  reason: string;
}

export interface SaveHomeworkResult extends HomeworkDayResult {
  created: boolean;
  changed: string[];
  retained: HomeworkRetainedNote[];
  /** 给用户看的人话说明（界面直接 toast）。 */
  warnings: string[];
}

export interface CopyHomeworkResult extends SaveHomeworkResult {
  copiedFrom: { date: string; sheetNodeId: string; subjectCount: number; itemCount: number };
}

export interface HomeworkHistorySheet {
  date: string;
  sheetNodeId: string;
  name: string;
  /**
   * 这一天的科目名（按 `sort_order`）—— 录入表单的**科目候选**来源。
   *
   * ⚠️ 这是同一份历史响应上新增的**只读字段**（没有新接口、没有新表、没有迁移）：
   * 服务端那条 SQL 本来就为 `subjectCount` 查了科目节点，只是多带了一列名字。
   * 候选的收集与排序是纯函数 `collectHomeworkSubjectNames()`（`@fht/shared`），
   * 页面只负责把"今天这一天的科目 + 历史各天的科目"按顺序喂进去。
   */
  subjects: string[];
  subjectCount: number;
  itemCount: number;
  doneCount: number;
  submittedCount: number;
  abandonedCount: number;
  remainingCount: number;
  completedSubjectCount: number;
  completed: boolean;
}

export interface HomeworkHistoryResult {
  studentId: string;
  from: string;
  to: string;
  timeZone: string;
  /** true = 请求区间超过 366 天，已被收敛（`from`/`to` 是**实际使用**的区间）。 */
  clamped: boolean;
  total: number;
  sheets: HomeworkHistorySheet[];
  summary: {
    sheetCount: number;
    subjectCount: number;
    itemCount: number;
    doneCount: number;
    submittedCount: number;
    abandonedCount: number;
    remainingCount: number;
    completedSheetCount: number;
  };
}

// ---------------------------------------------------------------- 超时队列（派生视图）

/**
 * 「超时」队列里的一条 = **一条打卡记录**（不是作业项）。
 *
 * 形状来自 `checkins.service.ts` 的 `OverdueCheckinEntry`，作业模块在此之上
 * 补了两个更好读的别名（见 `homework.service.ts` 的 `HomeworkOverdueResult`）：
 *  - `subjectName` = `nodeName`（作业页面上叫"科目"更自然）；
 *  - `date` = `dueDate`（这条作业属于哪一天）。
 *
 * ⚠️ 这两个别名**必须走别名**：`nodeName` / `dueDate` 在后端仍然存在，
 * 但页面读别名可以让"作业语义"与"通用打卡语义"分开，也方便后端将来只保留别名。
 */
export interface HomeworkOverdueEntry {
  /** **checkinId** —— 计时/完成/验收全都用它（不是 habitId）。 */
  id: string;
  habitId: string;
  habitName: string;
  nodeId: string;
  /** 科目名（后端给的 `nodeName` 别名）。 */
  subjectName: string;
  /** 这条作业属于哪一天（后端给的 `dueDate` 别名，'YYYY-MM-DD'）。 */
  date: string;
  dueAt: string;
  status: CheckinStatus;
  needApproval: boolean;
  /**
   * 这一项的分值；`null` = 服务端没给（**不吞成 0** —— 0 是"明确不计分"，
   * 与"拿不到"是两件事，界面文案因此分三支，见 `@fht/shared` 的
   * `homeworkApprovalActions()`）。
   */
  pointsPerUnit: number | null;
  /**
   * 当前使用者能不能验收这一条（服务端 `canApproveCheckin(actor)`）。
   *
   * ⚠️ 「超时」是跨天的派生视图：昨天提交的作业项**只**出现在这里。这个字段缺席时，
   * 家长在唯一看得见它的地方就没有任何验收入口（2026-10-15 用户实测报的 bug），
   * 所以它必须由服务端给，前端**不自己判角色**。
   */
  canApprove: boolean;
  isMakeup: boolean;
  estimatedSeconds: number | null;
  /**
   * **服务端确认过的**已用秒数。
   *
   * ⚠️ 2026-10-17 起它由服务端的 `recompute_checkin_seconds()` **实时重算**
   * （`running / paused / auto_paused` 三个状态，见 shared 的 `LIVE_TIMER_STATUSES`），
   * **不是** `checkin.actual_seconds` 那一列的旧快照。那一列只在
   * finish / skip / 自动暂停时才写回，pause / resume / 心跳都不写 ——
   * 生产实测某一项暂停过一次后，库里那一列还是 `0`，而 recompute 出来 358 秒。
   * 谁拿那一列当"已用时间"，孩子看到的就是 `00:00`。
   */
  actualSeconds: number;
  /**
   * 作业项的**目标量 / 单位**（`null` = 没设目标）。
   *
   * ⚠️ 队列行必须带上它们：孩子从「超时 / 还有效」点「完成」时用的是**同一个**
   * 完成弹窗，"有目标值就必须填完成量"这条校验（`lib/finishAmount.ts`）要靠它。
   * 拿不到时按 `null`（= 不强制填），⛔ 不要默认成 1 —— 那会替孩子填一个假的完成量。
   */
  targetValue: number | null;
  unit: string | null;
  startedAt: string | null;
  finishedAt: string | null;
  valueDone: number | null;
  note: string;
  skipReason: string;
  pointsAwarded: boolean;
  /** 逾期多久（秒）—— 由**服务端**算好，前端不必自己算时区。 */
  overdueSeconds: number;
}

export interface HomeworkOverdueResult {
  /** 已按 `dueAt` **倒序**（最新截止的在最上面）—— 排序由服务端保证。 */
  entries: HomeworkOverdueEntry[];
  total: number;
  /** 判定"超时"所用的服务端时间。 */
  serverNow: string;
}

// ---------------------------------------------------------------- 还有效（还没到期）

/**
 * 「**还有效**」队列里的一条 = 一条**还没到期**的未完成作业项。
 *
 * 它为什么存在（用户原话）：
 * > 昨天是周五，录了一批次截止日期为周日的作业……今天来看的时候，他点开来看想点"完成"，
 * > 就发现这两项作业的记录都没有了 —— 不管是当天的作业还是超时的作业都没有。
 *
 * 两个视图各用一个字段筛（「今天的作业」按 `checkin.due_date` 布置日，
 * 「超时」按 `checkin.due_at < now`），而"截止日期"上线后这两个字段不再相等，
 * 于是"昨天布置、明天截止、还没做完"的项掉进了缝里。这一份就是那一格。
 *
 * ⚠️ 与 `HomeworkOverdueEntry` 的**两处实质差别**（不是命名差别）：
 *  1. `date`（**布置日**）与 `deadlineDate`（**截止日**）通常**不相等**，
 *     所以界面必须把两天都说出来（`badge` 已由服务端算好，见下）；
 *  2. 没有"已超时"：这一份按定义全都没到期（服务端的 `overdueSeconds` 恒为 0）。
 */
export interface HomeworkOpenEntry extends HomeworkOverdueEntry {
  /**
   * **截止日**（`node.due_date ?? checkin.due_date`，'YYYY-MM-DD'）。
   *
   * ⚠️ 不是 `date`：`date` 是**布置日**（周五），这一个可能是周日。
   * 两者不同正是这条作业掉进缝隙的原因。
   */
  deadlineDate: string;
  /**
   * 「哪天布置 · 哪天截止」的徽章文案，**服务端算好的逐字结果**：
   * `📅 周五 10-16 布置 · 周日 10-18 截止`。
   *
   * 唯一实现是 `@fht/shared` 的 `describeHomeworkOpenBadge()`；前端拿不到时
   * 用它（同一个函数）就地补一份，绝不自己拼字符串。
   */
  badge: string;
}

export interface HomeworkOpenResult {
  /** 已按 `dueAt` **正序**（越早到期越靠前）—— 排序由服务端保证，前端不重排。 */
  entries: HomeworkOpenEntry[];
  total: number;
  serverNow: string;
  /** 判定用的"今天"（家庭时区，'YYYY-MM-DD'）。 */
  today: string;
}

// ---------------------------------------------------------------- 写操作入参

/** 一条作业项（PUT 的 body 形状，与 shared 的 `homeworkItemInputSchema` 同形）。 */
export interface HomeworkItemInput {
  name: string;
  pointsPerUnit: number;
  targetValue: number | null;
  unit: string | null;
}

/** 一个科目（`dueTime` 与 `durationMinutes` **只能填一个**）。 */
export interface HomeworkSubjectInput {
  name: string;
  startTime: string | null;
  dueTime: string | null;
  durationMinutes: number | null;
  /**
   * **截止日期**（'YYYY-MM-DD'）；`null` = 与布置日同一天（服务端的兜底语义）。
   *
   * 默认策略（周五/周六布置 → 周日）在**表单构建时**就算好了（见 `@fht/shared`
   * 的 `defaultHomeworkDueDate()`），这里只负责把"家长看到的那一天"原样送上去。
   */
  dueDate: string | null;
  items: HomeworkItemInput[];
}

export interface SaveHomeworkInput {
  /** 作业单名称；省略时服务端用「<日期> 的作业」。 */
  name?: string;
  subjects: HomeworkSubjectInput[];
}

// ---------------------------------------------------------------- 归一化

function asString(value: unknown, fallback = ''): string {
  return typeof value === 'string' ? value : fallback;
}

function asStringOrNull(value: unknown): string | null {
  return typeof value === 'string' && value !== '' ? value : null;
}

function asBoolean(value: unknown): boolean {
  return value === true;
}

function normalizeDueTimeSource(value: unknown): HomeworkDueTimeSource {
  return value === 'explicit' || value === 'duration' || value === 'dayEnd' || value === 'none'
    ? value
    : 'none';
}

function normalizeCheckin(raw: unknown): HomeworkCheckinView | null {
  if (raw === null || typeof raw !== 'object') return null;
  const record = raw as Record<string, unknown>;
  const checkinId = asString(record.checkinId);
  if (!checkinId) return null;

  return {
    checkinId,
    status: checkinStatusOf(record.status),
    estimatedSeconds: toOptionalNumber(record.estimatedSeconds),
    actualSeconds: toNumber(record.actualSeconds, 0),
    dueAt: asString(record.dueAt),
    startedAt: asStringOrNull(record.startedAt),
    finishedAt: asStringOrNull(record.finishedAt),
    valueDone: toOptionalNumber(record.valueDone),
    note: asString(record.note),
    skipReason: asString(record.skipReason),
    isMakeup: asBoolean(record.isMakeup),
    pointsAwarded: asBoolean(record.pointsAwarded),
    overdue: asBoolean(record.overdue),
    overdueSeconds: toNumber(record.overdueSeconds, 0),
  };
}

function normalizeCompletion(raw: unknown): SubjectCompletion {
  const record = (raw ?? {}) as Record<string, unknown>;
  const total = toNumber(record.total, 0);
  const done = toNumber(record.done, 0);
  const abandoned = toNumber(record.abandoned, 0);
  const submitted = toNumber(record.submitted, 0);
  return {
    total,
    done,
    submitted,
    abandoned,
    remaining: toNumber(record.remaining, Math.max(0, total - done - abandoned)),
    completed: asBoolean(record.completed),
  };
}

function normalizeItem(raw: unknown, subjectNodeId: string): HomeworkItemView | null {
  if (raw === null || typeof raw !== 'object') return null;
  const record = raw as Record<string, unknown>;
  const habitId = asString(record.habitId);
  if (!habitId) return null;

  const active = record.active === undefined ? true : asBoolean(record.active);
  const checkin = normalizeCheckin(record.checkin);

  return {
    habitId,
    subjectNodeId: asString(record.subjectNodeId, subjectNodeId),
    name: asString(record.name, '未命名作业'),
    pointsPerUnit: toNumber(record.pointsPerUnit, 0),
    targetValue: toOptionalNumber(record.targetValue),
    unit: asStringOrNull(record.unit),
    needApproval: record.needApproval === undefined ? true : asBoolean(record.needApproval),
    active,
    // 后端同时给 active 与 retained；缺失时按 active 反推（两者永远相反）
    retained: record.retained === undefined ? !active : asBoolean(record.retained),
    status: checkinStatusOf(record.status ?? checkin?.status),
    checkin,
    canCheckin: asBoolean(record.canCheckin),
    canApprove: asBoolean(record.canApprove),
  };
}

function normalizeSubject(raw: unknown): HomeworkSubjectView | null {
  if (raw === null || typeof raw !== 'object') return null;
  const record = raw as Record<string, unknown>;
  const nodeId = asString(record.nodeId);
  if (!nodeId) return null;

  const items = Array.isArray(record.items)
    ? record.items
        .map((item) => normalizeItem(item, nodeId))
        .filter((item): item is HomeworkItemView => item !== null)
    : [];

  const active = record.active === undefined ? true : asBoolean(record.active);

  return {
    nodeId,
    name: asString(record.name, '未命名科目'),
    startTime: asStringOrNull(record.startTime),
    dueTime: asStringOrNull(record.dueTime),
    durationMinutes: toOptionalNumber(record.durationMinutes),
    dueTimeSource: normalizeDueTimeSource(record.dueTimeSource),
    // 缺失 → 空串（= 与布置日同一天）。**不猜**默认策略：猜错就会显示一个假的截止日。
    dueDate: asString(record.dueDate),
    sortOrder: toNumber(record.sortOrder, 0),
    active,
    retained: record.retained === undefined ? !active : asBoolean(record.retained),
    items,
    completion: normalizeCompletion(record.completion),
  };
}

function normalizeSummary(raw: unknown, subjects: HomeworkSubjectView[]): HomeworkSummary {
  const record = (raw ?? {}) as Record<string, unknown>;
  const itemStatuses = subjects.flatMap((subject) => subject.items.map((item) => item.status));
  const fallbackTotal = itemStatuses.length;
  const fallbackDone = itemStatuses.filter((status) => status === 'done').length;
  const fallbackAbandoned = itemStatuses.filter((status) => status === 'skipped').length;
  const fallbackSubmitted = itemStatuses.filter((status) => status === 'submitted').length;

  const subjectCount = toNumber(record.subjectCount, subjects.length);
  const itemCount = toNumber(record.itemCount, fallbackTotal);
  const doneCount = toNumber(record.doneCount, fallbackDone);
  const abandonedCount = toNumber(record.abandonedCount, fallbackAbandoned);
  const completedSubjectCount = toNumber(
    record.completedSubjectCount,
    subjects.filter((subject) => subject.completion.completed).length,
  );

  return {
    subjectCount,
    itemCount,
    doneCount,
    submittedCount: toNumber(record.submittedCount, fallbackSubmitted),
    abandonedCount,
    remainingCount: toNumber(record.remainingCount, fallbackTotal - fallbackDone - fallbackAbandoned),
    completedSubjectCount,
    allCompleted: record.allCompleted === undefined
      ? subjectCount > 0 && completedSubjectCount === subjectCount
      : asBoolean(record.allCompleted),
  };
}

function normalizeSheet(raw: unknown): HomeworkSheetView | null {
  if (raw === null || typeof raw !== 'object') return null;
  const record = raw as Record<string, unknown>;
  const nodeId = asString(record.nodeId);
  if (!nodeId) return null;
  return {
    nodeId,
    date: asString(record.date),
    name: asString(record.name),
    createdAt: asString(record.createdAt),
  };
}

function normalizeRetained(raw: unknown): HomeworkRetainedNote[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((entry): HomeworkRetainedNote | null => {
      if (entry === null || typeof entry !== 'object') return null;
      const record = entry as Record<string, unknown>;
      const kind = record.kind === 'subject' ? 'subject' : 'item';
      return {
        kind,
        name: asString(record.name),
        subjectName: asStringOrNull(record.subjectName),
        nodeId: asStringOrNull(record.nodeId),
        habitId: asStringOrNull(record.habitId),
        reason: asString(record.reason),
      };
    })
    .filter((entry): entry is HomeworkRetainedNote => entry !== null);
}

function normalizeWarnings(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  return raw.filter((value): value is string => typeof value === 'string' && value !== '');
}

function normalizeDay(raw: unknown): HomeworkDayResult {
  const record = (raw ?? {}) as Record<string, unknown>;
  const subjects = Array.isArray(record.subjects)
    ? record.subjects
        .map(normalizeSubject)
        .filter((subject): subject is HomeworkSubjectView => subject !== null)
    : [];
  const permissions = (record.permissions ?? {}) as Record<string, unknown>;

  return {
    studentId: asString(record.studentId),
    date: asString(record.date),
    timeZone: asString(record.timeZone),
    exists: asBoolean(record.exists),
    sheet: normalizeSheet(record.sheet),
    subjects,
    summary: normalizeSummary(record.summary, subjects),
    permissions: {
      canEdit: asBoolean(permissions.canEdit),
      canCheckin: asBoolean(permissions.canCheckin),
      canApprove: asBoolean(permissions.canApprove),
    },
  };
}

// ---------------------------------------------------------------- 读

/** 取某天的作业单（`date` 省略 = 家庭时区下的今天）。 */
export async function getHomeworkDay(
  studentId: string,
  date?: string,
): Promise<HomeworkDayResult> {
  const data = await api.get<unknown>('/students/:id/homework'.replace(':id', studentId), {
    ...(date ? { date } : {}),
  });
  return normalizeDay(data);
}

/** 过往作业单列表（区间缺省为最近 30 天；服务端会收敛到 366 天内）。 */
export async function listHomeworkHistory(
  studentId: string,
  query: { from?: string; to?: string } = {},
): Promise<HomeworkHistoryResult> {
  const data = await api.get<unknown>('/students/:id/homework/history'.replace(':id', studentId), {
    ...(query.from ? { from: query.from } : {}),
    ...(query.to ? { to: query.to } : {}),
  });
  const record = (data ?? {}) as Record<string, unknown>;
  const sheets = Array.isArray(record.sheets)
    ? record.sheets
        .map((entry): HomeworkHistorySheet | null => {
          if (entry === null || typeof entry !== 'object') return null;
          const row = entry as Record<string, unknown>;
          const date = asString(row.date);
          const sheetNodeId = asString(row.sheetNodeId);
          if (!date || !sheetNodeId) return null;
          return {
            date,
            sheetNodeId,
            name: asString(row.name),
            /**
             * ⚠️ 缺失 / 不是数组时给空数组（不是 "未命名科目" 之类的兜底名）：
             * 这个字段只喂给"科目候选"下拉，凭空造一个名字出来会把它塞进候选里。
             * 老版本服务端（还没有这一列）与前端共存时，下拉退回"只有今天这一天的科目"。
             */
            subjects: Array.isArray(row.subjects)
              ? row.subjects.filter((value): value is string => typeof value === 'string')
              : [],
            subjectCount: toNumber(row.subjectCount, 0),
            itemCount: toNumber(row.itemCount, 0),
            doneCount: toNumber(row.doneCount, 0),
            submittedCount: toNumber(row.submittedCount, 0),
            abandonedCount: toNumber(row.abandonedCount, 0),
            remainingCount: toNumber(row.remainingCount, 0),
            completedSubjectCount: toNumber(row.completedSubjectCount, 0),
            completed: asBoolean(row.completed),
          };
        })
        .filter((entry): entry is HomeworkHistorySheet => entry !== null)
    : [];
  const summary = (record.summary ?? {}) as Record<string, unknown>;

  return {
    studentId: asString(record.studentId),
    from: asString(record.from),
    to: asString(record.to),
    timeZone: asString(record.timeZone),
    clamped: asBoolean(record.clamped),
    total: toNumber(record.total, sheets.length),
    sheets,
    summary: {
      sheetCount: toNumber(summary.sheetCount, sheets.length),
      subjectCount: toNumber(summary.subjectCount, 0),
      itemCount: toNumber(summary.itemCount, 0),
      doneCount: toNumber(summary.doneCount, 0),
      submittedCount: toNumber(summary.submittedCount, 0),
      abandonedCount: toNumber(summary.abandonedCount, 0),
      remainingCount: toNumber(summary.remainingCount, 0),
      completedSheetCount: toNumber(summary.completedSheetCount, 0),
    },
  };
}

// ---------------------------------------------------------------- 超时队列的读取

/**
 * 「超时」队列（`GET .../overdue`）—— **只读**，服务端不写任何状态。
 *
 * 判据（服务端）：`due_at < now()` 且状态 ∈
 * `(pending, running, paused, auto_paused, submitted)`。因此"超时"不是状态，
 * 而是一个会随孩子提交、家长通过而**随时消失**的派生视图 ——
 * 这一点是产品要求的核心：跨天的作业项永不被结算成 `missed`
 * （一次性任务），所以它会一直留在这里，**直到孩子完成、或家长点放弃**。
 *
 * 排序 `due_at` 倒序（最新截止的在最上面）由服务端保证，前端**不再重排**：
 * 一旦两边都排，"谁说了算"就再也说不清（而这条列表就是给人看的"待处理清单"）。
 */
function normalizeOverdueEntry(raw: unknown): HomeworkOverdueEntry | null {
  if (raw === null || typeof raw !== 'object') return null;
  const record = raw as Record<string, unknown>;
  const id = asString(record.id) || asString(record.checkinId);
  if (!id) return null;

  const dueDate = asString(record.dueDate);
  const nodeName = asString(record.nodeName);

  return {
    id,
    habitId: asString(record.habitId),
    habitName: asString(record.habitName, '未命名作业'),
    nodeId: asString(record.nodeId),
    // 后端给的是别名（subjectName / date），缺失时回落到原字段 —— 两边都认，
    // 这样后端即使只保留原始字段名，这一页也不会静默变空
    subjectName: asString(record.subjectName) || nodeName || '未命名科目',
    date: asString(record.date) || dueDate,
    dueAt: asString(record.dueAt),
    status: checkinStatusOf(record.status),
    needApproval: asBoolean(record.needApproval),
    // ⚠️ `toOptionalNumber`（拿不到 = null）而不是 `toNumber(..., 0)`：
    // 分值为 0 是"这一项明确不计分"，与"服务端没给这个字段"必须分得开
    // —— 吞成 0 会让界面把"不知道"说成"不计分"（见 HomeworkOverdueEntry 的注释）。
    pointsPerUnit: toOptionalNumber(record.pointsPerUnit),
    // 拿不到一律 false：宁可少显示一个按钮，也不显示一个"点了会失败"的按钮
    canApprove: asBoolean(record.canApprove),
    isMakeup: asBoolean(record.isMakeup),
    estimatedSeconds: toOptionalNumber(record.estimatedSeconds),
    actualSeconds: toNumber(record.actualSeconds, 0),
    // ⚠️ `toOptionalNumber`（拿不到 = null）而不是 `toNumber(..., 0)`：
    // 目标量缺席是"这一项没有量化目标"（完成量可以不填），`0` 会变成"目标 0"。
    targetValue: toOptionalNumber(record.targetValue),
    unit: asStringOrNull(record.unit),
    startedAt: asStringOrNull(record.startedAt),
    finishedAt: asStringOrNull(record.finishedAt),
    valueDone: toOptionalNumber(record.valueDone),
    note: asString(record.note),
    skipReason: asString(record.skipReason),
    pointsAwarded: asBoolean(record.pointsAwarded),
    overdueSeconds: toNumber(record.overdueSeconds, 0),
  };
}

/** 「超时」队列：未终结且已过截止时刻的**作业项**，按截止时间倒序。 */
export async function listHomeworkOverdue(
  studentId: string,
  query: { limit?: number } = {},
): Promise<HomeworkOverdueResult> {
  const data = await api.get<unknown>('/students/:id/homework/overdue'.replace(':id', studentId), {
    ...(query.limit ? { limit: query.limit } : {}),
  });
  const record = (data ?? {}) as Record<string, unknown>;
  const entries = Array.isArray(record.entries)
    ? record.entries
        .map(normalizeOverdueEntry)
        .filter((entry): entry is HomeworkOverdueEntry => entry !== null)
    : [];

  return {
    entries,
    total: toNumber(record.total, entries.length),
    serverNow: asString(record.serverNow),
  };
}

/**
 * 「还有效」队列里一条的归一化。
 *
 * 复用 `normalizeOverdueEntry()`（**同一个形状**，见 `HomeworkOpenEntry extends
 * HomeworkOverdueEntry`），只补两个这一份独有的字段：
 *  - `deadlineDate`：缺省回落到 `date`（= 布置日 = "当天完成"的老形状，与
 *    `homeworkDueDateOf()` 的兜底同义）；
 *  - `badge`：服务端算好的逐字文案；万一服务端没给（老响应/字段改名），
 *    用**同一个** shared 函数就地补一份 —— 绝不在这里自己拼字符串。
 */
function normalizeOpenEntry(raw: unknown, today: string): HomeworkOpenEntry | null {
  const base = normalizeOverdueEntry(raw);
  if (!base) return null;
  const record = raw as Record<string, unknown>;
  const deadlineDate = asString(record.deadlineDate) || base.date;
  const badge =
    asString(record.badge) ||
    describeHomeworkOpenBadge({ planDate: base.date, deadlineDate, today });
  return { ...base, deadlineDate, badge };
}

/**
 * 「**还有效**」队列（`GET .../open`）—— **只读**，服务端不写任何状态。
 *
 * 判据（服务端）：`due_at >= now()` **且** `due_date < 今天` **且**状态未终结。
 * 与「超时」（`due_at < now()`）严格互补：同一条作业不会同时出现在两个队列里，
 * 而两者合起来覆盖了"所有未完成的项" —— 中间那条缝（用户报的 bug）因此不存在了。
 *
 * 排序 `due_at` **正序**（越早到期越靠前）由服务端保证，前端**不再重排**。
 */
export async function listHomeworkOpen(
  studentId: string,
  query: { limit?: number } = {},
): Promise<HomeworkOpenResult> {
  const data = await api.get<unknown>('/students/:id/homework/open'.replace(':id', studentId), {
    ...(query.limit ? { limit: query.limit } : {}),
  });
  const record = (data ?? {}) as Record<string, unknown>;
  const today = asString(record.today);
  const entries = Array.isArray(record.entries)
    ? record.entries
        .map((raw) => normalizeOpenEntry(raw, today))
        .filter((entry): entry is HomeworkOpenEntry => entry !== null)
    : [];

  return {
    entries,
    total: toNumber(record.total, entries.length),
    serverNow: asString(record.serverNow),
    today,
  };
}

// ---------------------------------------------------------------- 写

function normalizeSave(raw: unknown): SaveHomeworkResult {
  const day = normalizeDay(raw);
  const record = (raw ?? {}) as Record<string, unknown>;
  return {
    ...day,
    created: asBoolean(record.created),
    changed: Array.isArray(record.changed)
      ? record.changed.filter((value): value is string => typeof value === 'string')
      : [],
    retained: normalizeRetained(record.retained),
    warnings: normalizeWarnings(record.warnings),
  };
}

/** 整单保存（新建或覆盖该日的科目与作业项）—— **每天录入走这个**。 */
export async function saveHomework(
  studentId: string,
  date: string,
  input: SaveHomeworkInput,
): Promise<SaveHomeworkResult> {
  const data = await api.put<unknown>(
    '/students/:id/homework'.replace(':id', studentId),
    input,
    { query: { date } },
  );
  return normalizeSave(data);
}

/** 「复制上次的结构」：`fromDate` 省略时服务端自动挑"目标日期之前最近的一份作业单"。 */
export async function copyHomework(
  studentId: string,
  input: { date: string; fromDate?: string },
): Promise<CopyHomeworkResult> {
  const data = await api.post<unknown>(
    '/students/:id/homework/copy'.replace(':id', studentId),
    input,
  );
  const saved = normalizeSave(data);
  const record = (data ?? {}) as Record<string, unknown>;
  const copied = (record.copiedFrom ?? {}) as Record<string, unknown>;
  return {
    ...saved,
    copiedFrom: {
      date: asString(copied.date),
      sheetNodeId: asString(copied.sheetNodeId),
      subjectCount: toNumber(copied.subjectCount, 0),
      itemCount: toNumber(copied.itemCount, 0),
    },
  };
}

// ---------------------------------------------------------------- 三个语义的共享判据

/**
 * 科目时间的**展示文案**（家长端查看页那一行、以及折叠摘要的对照口径）。
 *
 * ⚠️ 这是 `dayEnd` 语义的唯一出口：`dueTimeSource === 'dayEnd'` 且**截止日就是布置日**时
 * **绝不能**显示 23:59 —— 那是服务端为了"当天完成"落下的默认值，不是家长填的截止时刻。
 *
 * `planDate`（这一天的日期）是 2026-10-16 新增的：截止日可能与布置日**不同**
 * （周五布置、周日截止），那时必须把**哪一天**说出来，否则"当天完成"就是假话。
 * 不传它时（旧调用点）按"截止日 = 布置日"渲染，与改造前逐字相同。
 * 判据与文案都在 `@fht/shared` 的 `describeHomeworkDueText()`（**唯一一份**，
 * 家长端表单摘要、查看页、学生端都引用它）。
 */
export function describeSubjectSchedule(
  subject: {
    startTime: string | null;
    dueTime: string | null;
    durationMinutes: number | null;
    dueTimeSource: HomeworkDueTimeSource;
    /** 截止日期（缺失/空 = 与布置日同一天）。 */
    dueDate?: string | null;
  },
  planDate?: string,
): string {
  const parts: string[] = [];
  if (subject.startTime) parts.push(`${subject.startTime} 开始`);

  const dueText = describeHomeworkDueText({
    planDate: planDate ?? subject.dueDate ?? '',
    dueDate: subject.dueDate ?? null,
    dueTimeSource: subject.dueTimeSource,
    dueTime: subject.dueTime,
    durationMinutes: subject.durationMinutes,
  });
  if (dueText !== '') parts.push(dueText);
  return parts.join(' · ');
}

/**
 * 一个作业项是不是"空项"（没有打卡记录、也没到任何终态）。
 *
 * ⚠️ **不能**用"名字是不是新出现的"来判断：同名重新加回来会**复用原记录**
 * （包括原打卡记录），这是刻意的语义。所以判断依据只能是服务端给的
 * `status` 与 `checkin` —— 有记录就照原样显示，绝不当成新项渲染成空的。
 */
export function isHomeworkItemEmpty(item: HomeworkItemView): boolean {
  return item.checkin === null && item.status === 'pending';
}
